import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, CircularProgress, Grid,
  IconButton, InputAdornment, MenuItem, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField as MuiTextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import brokerCompensationApi from '../../services/brokerCompensationApi';
import StatusBadge from '../shared/StatusBadge';
import { useToast } from '../shared/Toast';
import LoadingSkeleton from '../shared/LoadingSkeleton';
import { useConfirmModal } from '../shared/ConfirmModal';
import FormProvider from '../form/FormProvider';
import TextField from '../form/TextField';
import NumberField from '../form/NumberField';
import CurrencyField from '../form/CurrencyField';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const formatCurrency = (value) => {
  if (value == null) return '—';
  return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const parts = dateStr.split('-');
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const compSchema = z.object({
  effectiveDate: z.string().min(1, 'Effective date is required'),
  percentage: z.union([z.number().min(0).max(5), z.literal('')]).refine(v => v !== '', { message: 'Percentage is required' }),
  dollarAmount: z.union([z.number().min(0), z.literal('')]).optional().default(''),
  minDollarAmount: z.union([z.number().min(0), z.literal('')]).optional().default(''),
  maxDollarAmount: z.union([z.number().min(0), z.literal('')]).optional().default(''),
});

const emptyDefaults = (defaultDate) => ({
  effectiveDate: defaultDate || tomorrowStr(),
  percentage: '',
  dollarAmount: '',
  minDollarAmount: '',
  maxDollarAmount: '',
});

const BrokerCompensationPanel = ({ companies = [], defaultCompanyId }) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState(defaultCompanyId);

  const brokerCompanyId = selectedCompanyId;
  const { data: compensations = [], isLoading } = brokerCompensationApi.endpoints.getCompensations.useQuery(brokerCompanyId, {
    skip: !brokerCompanyId,
  });
  const [createComp] = brokerCompensationApi.endpoints.createCompensation.useMutation();
  const [updateComp] = brokerCompensationApi.endpoints.updateCompensation.useMutation();
  const [deleteComp] = brokerCompensationApi.endpoints.deleteCompensation.useMutation();

  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmModalContainer } = useConfirmModal();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const compMethods = useForm({ resolver: zodResolver(compSchema), defaultValues: emptyDefaults() });

  const handleCompanyChange = (e) => {
    setSelectedCompanyId(e.target.value);
    setShowForm(false);
    setEditingId(null);
    compMethods.reset(emptyDefaults());
  };

  if (isLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  const activeComp = compensations.find((c) => c.isActive);
  const futureComps = compensations.filter((c) => !c.isActive && c.effectiveDate > todayStr());
  const pastComps = compensations.filter((c) => !c.isActive && c.effectiveDate <= todayStr());

  // For new comps (not editing), the earliest selectable date is the day after
  // the active comp's lockdown end date (if one exists), otherwise tomorrow.
  const minEffectiveDate = (() => {
    if (editingId) return tomorrowStr();
    if (activeComp?.lockdownEndDate) {
      const parts = activeComp.lockdownEndDate.split('-');
      const d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
      d.setDate(d.getDate() + 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    return tomorrowStr();
  })();

  const handleOpenCreate = () => {
    setEditingId(null);
    compMethods.reset(emptyDefaults(minEffectiveDate));
    setShowForm(true);
  };

  const handleOpenEdit = (comp) => {
    setEditingId(comp.id);
    compMethods.reset({
      effectiveDate: comp.effectiveDate,
      percentage: comp.percentage ?? '',
      dollarAmount: comp.dollarAmount ?? '',
      minDollarAmount: comp.minDollarAmount ?? '',
      maxDollarAmount: comp.maxDollarAmount ?? '',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    compMethods.reset(emptyDefaults());
  };

  const buildPayload = (data) => ({
    effectiveDate: data.effectiveDate,
    percentage: data.percentage !== '' ? parseFloat(data.percentage) : 0,
    dollarAmount: data.dollarAmount !== '' ? parseFloat(data.dollarAmount) : null,
    minDollarAmount: data.minDollarAmount !== '' ? parseFloat(data.minDollarAmount) : null,
    maxDollarAmount: data.maxDollarAmount !== '' ? parseFloat(data.maxDollarAmount) : null,
  });

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (editingId) {
        await updateComp({ brokerCompanyId, compensationId: editingId, ...buildPayload(data) }).unwrap();
        showToast('Compensation updated.');
      } else {
        await createComp({ brokerCompanyId, ...buildPayload(data) }).unwrap();
        showToast('Compensation created.');
      }
      handleCancel();
    } catch (err) {
      const msg = getAxiosErrorMessage(err, 'Failed to save compensation.');
      showToast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (comp) => {
    const confirmed = await confirm({
      title: 'Delete Compensation',
      message: `Are you sure you want to delete the compensation effective ${formatDate(comp.effectiveDate)}?`,
      confirmText: 'Delete',
      confirmClass: 'btn-danger',
    });
    if (!confirmed) return;

    try {
      await deleteComp({ brokerCompanyId, compensationId: comp.id }).unwrap();
      showToast('Compensation deleted.');
    } catch (err) {
      const msg = getAxiosErrorMessage(err, 'Failed to delete compensation.');
      showToast(msg, 'danger');
    }
  };

  return (
    <Box>
      {ToastContainer}
      {ConfirmModalContainer}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Compensation Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage compensation schedules per company / office</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>New Compensation</Button>
      </Stack>

      {/* Company Selector */}
      <Box sx={{ mb: 3, maxWidth: 360 }}>
        <MuiTextField select label="Broker Company" required fullWidth size="small" value={selectedCompanyId || ''} onChange={handleCompanyChange}>
          {companies.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}{c.isParent ? ' (Main Office)' : ''}</MenuItem>
          ))}
        </MuiTextField>
      </Box>

      {/* Active Compensation Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader avatar={<CheckCircleOutlineIcon color="success" />} title="Active Compensation" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
        {activeComp ? (
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 4, md: true }}>
                <Typography variant="caption" color="text.secondary">Effective Date</Typography>
                <Typography variant="body2" fontWeight={600}>{formatDate(activeComp.effectiveDate)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: true }}>
                <Typography variant="caption" color="text.secondary">Percentage</Typography>
                <Typography variant="h5" color="primary" fontWeight={700}>{activeComp.percentage}%</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: true }}>
                <Typography variant="caption" color="text.secondary">Dollar Amount</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCurrency(activeComp.dollarAmount)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: true }}>
                <Typography variant="caption" color="text.secondary">Min Amount</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCurrency(activeComp.minDollarAmount)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: true }}>
                <Typography variant="caption" color="text.secondary">Max Amount</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCurrency(activeComp.maxDollarAmount)}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        ) : (
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AttachMoneyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6">No active compensation</Typography>
              <Typography variant="body2" color="text.secondary">Create a compensation schedule with a future effective date. It will become active automatically.</Typography>
            </Box>
          </CardContent>
        )}
      </Card>

      {/* Create / Edit Form */}
      {showForm && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={editingId ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
            title={editingId ? 'Edit Compensation' : 'New Compensation'}
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
          />
          <FormProvider methods={compMethods} onSubmit={handleSave}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    name="effectiveDate" label="Effective Date" type="date" required size="small"
                    inputProps={{ min: minEffectiveDate }}
                    InputLabelProps={{ shrink: true }}
                    helperText={!editingId && activeComp?.lockdownEndDate
                      ? `Must be after lockdown period (${formatDate(activeComp.lockdownEndDate)})`
                      : 'Must be a future date'}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <NumberField
                    name="percentage" label="Percentage" required fullWidth size="small"
                    step={0.01} min={0} max={5}
                    placeholder="e.g. 2.5"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    helperText="Max 5%"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CurrencyField name="dollarAmount" label="Dollar Amount" placeholder="Optional" />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CurrencyField name="minDollarAmount" label="Minimum Amount" placeholder="Optional" />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CurrencyField name="maxDollarAmount" label="Maximum Amount" placeholder="Optional" />
                </Grid>
              </Grid>
            </CardContent>
            <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
              <Button type="submit" variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCancel} disabled={saving}>Cancel</Button>
            </Box>
          </FormProvider>
        </Card>
      )}

      {/* Upcoming / Future Compensations */}
      {futureComps.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader avatar={<ScheduleIcon color="info" />} title={`Upcoming (${futureComps.length})`} titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Effective Date</TableCell>
                  <TableCell>Percentage</TableCell>
                  <TableCell>Dollar Amount</TableCell>
                  <TableCell>Min Amount</TableCell>
                  <TableCell>Max Amount</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {futureComps.map((comp) => (
                  <TableRow key={comp.id}>
                    <TableCell>{formatDate(comp.effectiveDate)}</TableCell>
                    <TableCell><Typography variant="body2" fontWeight={700}>{comp.percentage}%</Typography></TableCell>
                    <TableCell>{formatCurrency(comp.dollarAmount)}</TableCell>
                    <TableCell>{formatCurrency(comp.minDollarAmount)}</TableCell>
                    <TableCell>{formatCurrency(comp.maxDollarAmount)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleOpenEdit(comp)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(comp)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Past Compensations */}
      {pastComps.length > 0 && (
        <Card>
          <CardHeader avatar={<HistoryIcon color="disabled" />} title={`History (${pastComps.length})`} titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Effective Date</TableCell>
                  <TableCell>Percentage</TableCell>
                  <TableCell>Dollar Amount</TableCell>
                  <TableCell>Min Amount</TableCell>
                  <TableCell>Max Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pastComps.map((comp) => (
                  <TableRow key={comp.id} sx={{ color: 'text.secondary' }}>
                    <TableCell sx={{ color: 'inherit' }}>{formatDate(comp.effectiveDate)}</TableCell>
                    <TableCell sx={{ color: 'inherit' }}>{comp.percentage}%</TableCell>
                    <TableCell sx={{ color: 'inherit' }}>{formatCurrency(comp.dollarAmount)}</TableCell>
                    <TableCell sx={{ color: 'inherit' }}>{formatCurrency(comp.minDollarAmount)}</TableCell>
                    <TableCell sx={{ color: 'inherit' }}>{formatCurrency(comp.maxDollarAmount)}</TableCell>
                    <TableCell><StatusBadge variant="secondary">Expired</StatusBadge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};

export default BrokerCompensationPanel;
